import React, { useState, useMemo, useEffect } from 'react';
import { useCommandsStore } from '../store/commandsStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PromptImprover from '../components/PromptImprover';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, RotateCcw, Sparkles, ArrowUp, ArrowDown, X, Copy } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const CommandManagement = ({ onClose, initialAction }) => {
  const { commands, addCommand, updateCommand, deleteCommand, resetToDefaults } = useCommandsStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCommand, setEditingCommand] = useState(null);
  // T-034: `steps` is the source of truth in the form; a 1-step command edits
  // exactly like before. `category` is shared by all steps of the command.
  // T-236: el checkbox "plantilla de chat" se eliminó; todo comando de texto
  // es usable como plantilla por defecto.
  const [formData, setFormData] = useState({ title: '', category: 'Texto', steps: [{ instruction: '', model: '', advisors: [] }] });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, commandId: null, commandTitle: '' });
  const [resetConfirm, setResetConfirm] = useState(false);
  // T-212 (ítem 17): filtros client-side sobre la lista de comandos.
  const [filterText, setFilterText] = useState('');
  const [filterCategory, setFilterCategory] = useState('Todas');


  // T-233: el catálogo de modelos por paso (modelo + asesores) se mudó a una
  // feature nueva. Aquí una plantilla es un texto único y los pasos
  // macros ya no llevan selector de modelo/asesores.

  const handleOpenDialog = (command = null) => {
    if (command) {
      setEditingCommand(command);
      const steps = Array.isArray(command.steps) && command.steps.length > 0
        ? command.steps.map((s) => ({ instruction: s.instruction || '', model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
        : [{ instruction: command.instruction || '', model: '', advisors: [] }];
      setFormData({ title: command.title, category: command.category || 'Texto', steps });
    } else {
      setEditingCommand(null);
      setFormData({ title: '', category: 'Texto', steps: [{ instruction: '', model: '', advisors: [] }] });
    }
    setDialogOpen(true);
  };

  // T-034 step editors (add / remove / reorder / edit).
  const addStep = () => {
    setFormData((prev) => ({ ...prev, steps: [...prev.steps, { instruction: '', model: '', advisors: [] }] }));
  };

  const removeStep = (index) => {
    setFormData((prev) => {
      if (prev.steps.length <= 1) return prev; // always keep at least one step
      return { ...prev, steps: prev.steps.filter((_, i) => i !== index) };
    });
  };

  const updateStep = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === index ? { ...s, instruction: value } : s))
    }));
  };

  const moveStep = (index, direction) => {
    setFormData((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...prev, steps };
    });
  };

  const handleSave = () => {
    // T-236: el checkbox "plantilla de chat" se eliminó; todo comando de texto
    // es usable como plantilla por defecto. Se mantiene el editor multi-paso.
    const cleanedSteps = formData.steps
      .map((s) => ({ instruction: (s.instruction || '').trim(), model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
      .filter((s) => s.instruction.length > 0);

    if (!formData.title.trim() || cleanedSteps.length === 0) {
      toast.error('Título y al menos un paso con instrucción son requeridos');
      return;
    }

    const payload = { title: formData.title, category: formData.category, steps: cleanedSteps };

    if (editingCommand) {
      updateCommand(editingCommand.id, payload);
      toast.success('Comando actualizado');
    } else {
      addCommand(payload);
      toast.success('Comando creado');
    }

    setDialogOpen(false);
    setFormData({ title: '', category: 'Texto', steps: [{ instruction: '', model: '', advisors: [] }] });
  };

  const handleDelete = (command) => {
    setDeleteConfirm({
      open: true,
      commandId: command.id,
      commandTitle: command.title
    });
  };

  const confirmDelete = () => {
    deleteCommand(deleteConfirm.commandId);
    toast.success('Comando eliminado');
    setDeleteConfirm({ open: false, commandId: null, commandTitle: '' });
  };

  // T-037: system commands are read-only; "Duplicar" makes an editable copy.
  const handleDuplicate = (command) => {
    const steps = Array.isArray(command.steps) && command.steps.length > 0
      ? command.steps.map((s) => ({ instruction: s.instruction || '', model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
      : [{ instruction: command.instruction || '', model: '', advisors: [] }];
    addCommand({ title: `${command.title} (copia)`, category: command.category || 'Texto', steps });
    toast.success('Comando duplicado');
  };

  const handleReset = () => {
    setResetConfirm(true);
  };

  const confirmReset = () => {
    resetToDefaults();
    toast.success('Comandos restaurados');
    setResetConfirm(false);
  };

  // T-212 (ítem 17): categorías derivadas de los comandos presentes (no
  // hardcodeadas). Si mañana aparece una nueva categoría, aparece en el filtro.
  const availableCategories = useMemo(() => {
    const set = new Set();
    (commands || []).forEach((c) => { if (c.category) set.add(c.category); });
    return Array.from(set);
  }, [commands]);

  // T-212 (ítem 17): filtrado client-side con useMemo. Busca por título e
  // instrucción (case-insensitive), filtra por categoría.
  const filteredCommands = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return (commands || []).filter((cmd) => {
      if (q) {
        const titleMatch = (cmd.title || '').toLowerCase().includes(q);
        const instructionText = Array.isArray(cmd.steps) && cmd.steps.length > 0
          ? cmd.steps.map((s) => s.instruction || '').join(' ')
          : (cmd.instruction || '');
        const instrMatch = instructionText.toLowerCase().includes(q);
        if (!titleMatch && !instrMatch) return false;
      }
      if (filterCategory !== 'Todas' && (cmd.category || 'Texto') !== filterCategory) return false;
      return true;
    });
  }, [commands, filterText, filterCategory]);

  const hasActiveFilter = filterText.trim() !== '' || filterCategory !== 'Todas';

  const clearFilters = () => {
    setFilterText('');
    setFilterCategory('Todas');
  };

  // T-212 (ítem 19): permite abrir el gestor directamente en modo crear/editar
  // cuando se invoca desde el popover de plantillas del chat. initialAction se
  // pasa desde EditorPage y se limpia al cerrar el diálogo.
  useEffect(() => {
    if (!initialAction) return;
    if (initialAction.type === 'create') {
      handleOpenDialog(null);
    } else if (initialAction.type === 'edit' && initialAction.commandId) {
      const cmd = (commands || []).find((c) => c.id === initialAction.commandId);
      if (cmd) handleOpenDialog(cmd);
    }
  }, [initialAction]); // deps deliberadas: solo reaccionar a initialAction

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-sky-600" />
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Administrar Comandos Rápidos de IA
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="reset-commands-button"
            variant="outline"
            onClick={handleReset}
            className="glass border-0"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restaurar
          </Button>
          <Button
            data-testid="add-command-button"
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Comando
          </Button>
        </div>
      </div>

      {/* T-212 (ítem 17): barra de filtros sobre la lista de comandos */}
      <div className="flex flex-wrap items-center gap-2 glass rounded-lg p-3">
        <div className="relative flex-1 min-w-[180px]">
          <Input
            data-testid="cmd-filter-text"
            placeholder="Buscar por título o instrucción…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="pr-7"
          />
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              title="Limpiar texto"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="cmd-filter-category" className="w-[140px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Todas">Todas</SelectItem>
            {availableCategories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasActiveFilter && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-xs"
              title="Limpiar filtros"
            >
              <X className="w-4 h-4" />
              Limpiar
            </Button>
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
              {filteredCommands.length} de {commands.length} comandos
            </span>
          </>
        )}
      </div>

      {/* Commands List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredCommands.map((command) => (
          <Card
            key={command.id}
            data-testid={`command-card-${command.id}`}
            className="glass border-0 hover:shadow-lg transition-all"
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-lg">{command.title}</h3>
                  <div className="flex items-center gap-1">
                  {command.system && (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-800">
                      Sistema
                    </Badge>
                  )}
                  {Array.isArray(command.steps) && command.steps.length >= 2 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800">
                      Macro · {command.steps.length} pasos
                    </Badge>
                  )}
                  <Badge variant="outline" className={
                    command.category === 'Imagen' ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800' :
                    command.category === 'Descripción' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800' :
                    'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:border-sky-800'
                  }>
                    {command.category || 'Texto'}
                  </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  {command.system ? (
                    <Button
                      data-testid={`duplicate-command-${command.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(command)}
                      className="h-8 w-8 p-0 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                      title="Duplicar como comando editable"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        data-testid={`edit-command-${command.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(command)}
                        className="h-8 w-8 p-0 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-command-${command.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(command)}
                        className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
                {command.instruction}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {commands.length === 0 && (
        <div className="text-center py-12">
          <Sparkles className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-600 dark:text-slate-300 mb-4">No hay comandos configurados</p>
          <Button onClick={() => handleOpenDialog()}>
            Crear primer comando
          </Button>
        </div>
      )}

      {/* T-212 (ítem 17): sin resultados tras filtrar (distinto de "no hay comandos"). */}
      {commands.length > 0 && filteredCommands.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-600 dark:text-slate-300 mb-4">No hay comandos que coincidan con los filtros.</p>
          <Button variant="outline" onClick={clearFilters}>
            <X className="w-4 h-4 mr-2" />
            Limpiar filtros
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass border-0 max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCommand ? 'Editar Comando' : 'Nuevo Comando'}
            </DialogTitle>
            <DialogDescription>
              Un comando sirve de dos maneras: ejecutarlo sobre texto con Acción Rápida IA, o adjuntarlo como plantilla en el chat para dar estilo y personalidad a la sesión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Título del comando</label>
              <Input
                data-testid="command-title-input"
                placeholder="Ej: Escritor Creativo"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Categoría</label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger data-testid="command-category-select">
                  <SelectValue placeholder="Selecciona una categoría..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Texto">Texto</SelectItem>
                  <SelectItem value="Imagen">Imagen</SelectItem>
                  <SelectItem value="Descripción">Descripción</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* T-236: el checkbox "plantilla de chat" se eliminó; todo comando de
                texto es usable como plantilla por defecto. Texto informativo. */}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Todo comando de texto puede adjuntarse como plantilla en el chat desde el popover «Plantilla»: su texto (los pasos unidos) se antepone a cada mensaje; ahí no ejecuta pasos, no cambia de modelo y no usa asesores. El Prompt del documento sigue mandando. Para secuencias con modelo propio y asesores usa Flujos IA.
            </p>
            {/* T-236: el editor multi-paso queda SIEMPRE para todos los comandos
                (ya no hay modo condicional de plantilla de un solo texto). */}
            <div className="space-y-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Los pasos se ejecutan en orden al usar el comando desde Acción Rápida IA. Para pasos con modelo propio y asesores usa Flujos IA.
                </p>
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">
                    {formData.steps.length > 1
                      ? `Pasos del macro (${formData.steps.length})`
                      : 'Instrucción para la IA'}
                  </label>
                  {formData.steps.length > 1 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Se ejecutan en orden; cada paso recibe el resultado del anterior.
                    </span>
                  )}
                </div>

                {formData.steps.map((step, index) => (
                  <div
                    key={index}
                    data-testid={`command-step-${index}`}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Paso {index + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <PromptImprover
                          currentPrompt={step.instruction}
                          onImproved={(improved) => updateStep(index, improved)}
                        />
                        <Button
                          type="button"
                          data-testid={`step-up-${index}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => moveStep(index, -1)}
                          disabled={index === 0}
                          className="h-7 w-7 p-0"
                          title="Subir paso"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          data-testid={`step-down-${index}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => moveStep(index, 1)}
                          disabled={index === formData.steps.length - 1}
                          className="h-7 w-7 p-0"
                          title="Bajar paso"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          data-testid={`step-remove-${index}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStep(index)}
                          disabled={formData.steps.length <= 1}
                          className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-300"
                          title="Eliminar paso"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      data-testid={index === 0 ? 'command-instruction-input' : `command-step-input-${index}`}
                      placeholder="Ej: Reescribe el siguiente texto de manera más creativa..."
                      value={step.instruction}
                      onChange={(e) => updateStep(index, e.target.value)}
                      rows={index === 0 ? 5 : 4}
                      className="resize-none"
                    />
                  </div>
                ))}

                <Button
                  type="button"
                  data-testid="add-step-button"
                  variant="outline"
                  size="sm"
                  onClick={addStep}
                  className="w-full glass border-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar paso
                </Button>

                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formData.category === 'Imagen'
                    ? 'Esta instrucción se utilizará para generar imágenes basadas en el texto ingresado.'
                    : formData.category === 'Descripción'
                    ? 'Esta instrucción se utilizará para describir imágenes.'
                    : 'El texto seleccionado se agregará al final del primer paso. Un comando con 2+ pasos se ejecuta como macro secuencial.'}
                </p>
              </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                data-testid="save-command-button"
                onClick={handleSave}
                className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
              >
                {editingCommand ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Command Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => !open && setDeleteConfirm({ open: false, commandId: null, commandTitle: '' })}
        title="Eliminar comando"
        description={`¿Estás seguro de que deseas eliminar el comando "${deleteConfirm.commandTitle}"?`}
        onConfirm={confirmDelete}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />

      {/* Reset Commands Confirmation */}
      <ConfirmDialog
        open={resetConfirm}
        onOpenChange={setResetConfirm}
        title="Restaurar comandos"
        description="¿Eliminar todos tus comandos personalizados? Los Comandos Rápidos de IA base se conservan."
        onConfirm={confirmReset}
        confirmText="Restaurar"
        cancelText="Cancelar"
        variant="destructive"
      />
    </div>
  );
};

export default CommandManagement;
